import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Image as ImageIcon, Loader2, AlertCircle, CheckCircle2, UserPlus, UserMinus, RotateCw, RotateCcw, Crop, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { getApiBaseUrl } from '../../services/cloudApi';
import { getPlusEmailColor } from '../../utils/userDisplay';
import Username, { getDisplayName } from '../Common/Username';
import { rotateImage, cropImageToCircle, createPreviewUrl, revokePreviewUrl, blobToFile } from '../../utils/imageEditor';
import { useMembership } from '../../hooks/useMembership';
import { UPGRADE_MESSAGES } from '../../utils/membershipConstants';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const CreateGroupModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('chat');
  const { authToken, isPlusUser } = useAuth();
  const { friends, createGroup } = useChat();
  const { limits } = useMembership();
  const maxUploadBytes = limits?.maxUploadBytes ?? 15 * 1024 * 1024;
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]); // Array of friend emails
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropPreviewUrl, setCropPreviewUrl] = useState(null);
  const [cropRotation, setCropRotation] = useState(0);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const cropImageRef = useRef(null);
  const cropContainerRef = useRef(null);
  const cropCircleSize = 300; // Yuvarlak kırpma alanının boyutu

  // Filter friends (only active friends, not deleted)
  const availableFriends = friends.filter(f => !f.isDeleted);

  // Check group creation limit
  useEffect(() => {
    if (isOpen && !isPlusUser) {
      // Limit will be checked on backend, but we can show warning here
      // Backend will enforce the limit
    }
  }, [isOpen, isPlusUser]);

  // Handle avatar selection
  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError(t('createGroupModal.selectImageError'));
      return;
    }

    if (file.size > maxUploadBytes) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('createGroupModal.fileSizeLimitTitle'), message: UPGRADE_MESSAGES.uploadLimit },
      }));
      return;
    }

    setAvatarFile(file);
    setCropRotation(0);
    setImagePosition({ x: 0, y: 0 });
    setImageScale(1);
    
    // Create preview and show crop modal
    createPreviewUrl(file).then((url) => {
      setCropPreviewUrl(url);
      setShowCropModal(true);
    }).catch((error) => {
      console.error('Failed to create preview:', error);
      setError(t('createGroupModal.previewErrorFallback'));
    });
  };

  // Handle rotation in crop modal
  const handleRotate = async (direction) => {
    if (!avatarFile || isProcessing) return;
    
    setIsProcessing(true);
    try {
      const rotatedBlob = await rotateImage(avatarFile, cropRotation, direction);
      const rotatedFile = blobToFile(rotatedBlob, avatarFile.name);
      
      // Update preview
      if (cropPreviewUrl) {
        revokePreviewUrl(cropPreviewUrl);
      }
      const newPreviewUrl = await createPreviewUrl(rotatedFile);
      setCropPreviewUrl(newPreviewUrl);
      setAvatarFile(rotatedFile);
      setCropRotation((prev) => (prev + (direction * 90) + 360) % 360);
      // Reset position after rotation
      setImagePosition({ x: 0, y: 0 });
    } catch (error) {
      console.error('Rotation error:', error);
      setError(t('createGroupModal.rotateErrorFallback'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Initialize image position and scale when crop modal opens
  useEffect(() => {
    if (showCropModal && cropImageRef.current) {
      // Wait for image to load, then calculate initial scale and position
      const img = cropImageRef.current;
      if (img.complete) {
        calculateInitialTransform(img);
      } else {
        img.onload = () => calculateInitialTransform(img);
      }
    }
  }, [showCropModal, cropPreviewUrl]);

  const calculateInitialTransform = (img) => {
    // Wait a bit for layout to settle
    setTimeout(() => {
      if (!img || !img.offsetWidth || !img.offsetHeight) return;
      
      // Calculate scale to fit the circle
      const imgAspect = img.naturalWidth / img.naturalHeight;
      
      let initialScale = 1;
      if (imgAspect > 1) {
        // Landscape: fit height
        initialScale = cropCircleSize / img.offsetHeight;
      } else {
        // Portrait or square: fit width
        initialScale = cropCircleSize / img.offsetWidth;
      }
      
      // Add some padding (scale up a bit so user can adjust)
      initialScale = initialScale * 1.2;
      
      setImageScale(Math.max(1, Math.min(initialScale, 2)));
      setImagePosition({ x: 0, y: 0 });
    }, 100);
  };

  // Handle mouse down for image dragging
  const handleCropMouseDown = (e) => {
    if (isProcessing) return;
    e.preventDefault();
    setIsDragging(true);
    const rect = cropImageRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({ 
        x: e.clientX - imagePosition.x, 
        y: e.clientY - imagePosition.y 
      });
    }
  };

  const handleCropMouseMove = (e) => {
    if (!isDragging || isProcessing) return;
    e.preventDefault();
    
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleCropMouseUp = () => {
    setIsDragging(false);
  };

  // Handle zoom
  const handleZoom = (delta) => {
    const newScale = Math.max(0.5, Math.min(3, imageScale + delta));
    setImageScale(newScale);
  };

  // Apply crop - create circular cropped image
  const handleApplyCrop = async () => {
    if (!avatarFile || isProcessing || !cropImageRef.current) return;
    
    setIsProcessing(true);
    try {
      // Create a canvas to draw the circular cropped image
      const canvas = document.createElement('canvas');
      canvas.width = cropCircleSize;
      canvas.height = cropCircleSize;
      const ctx = canvas.getContext('2d');
      
      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(cropCircleSize / 2, cropCircleSize / 2, cropCircleSize / 2, 0, 2 * Math.PI);
      ctx.clip();
      
      // Load the image
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = cropPreviewUrl;
      });
      
      const imgElement = cropImageRef.current;
      const imgNaturalWidth = img.naturalWidth;
      const imgNaturalHeight = img.naturalHeight;
      const imgDisplayWidth = imgElement.offsetWidth;
      const imgDisplayHeight = imgElement.offsetHeight;
      
      // Calculate scale from display to natural
      const scaleX = imgNaturalWidth / imgDisplayWidth;
      const scaleY = imgNaturalHeight / imgDisplayHeight;
      
      // The circle center in display coordinates (relative to image center)
      // imagePosition is the offset from center, so we need to account for that
      const circleCenterDisplayX = -imagePosition.x / imageScale;
      const circleCenterDisplayY = -imagePosition.y / imageScale;
      
      // Convert to natural coordinates
      const circleCenterNaturalX = (imgDisplayWidth / 2 + circleCenterDisplayX) * scaleX;
      const circleCenterNaturalY = (imgDisplayHeight / 2 + circleCenterDisplayY) * scaleY;
      
      // Calculate the radius in natural coordinates
      const circleRadiusDisplay = cropCircleSize / 2;
      const circleRadiusNatural = (circleRadiusDisplay / imageScale) * Math.min(scaleX, scaleY);
      
      // Calculate source rectangle (square that fits the circle)
      const sourceSize = circleRadiusNatural * 2;
      const sourceX = Math.max(0, Math.min(circleCenterNaturalX - circleRadiusNatural, imgNaturalWidth - sourceSize));
      const sourceY = Math.max(0, Math.min(circleCenterNaturalY - circleRadiusNatural, imgNaturalHeight - sourceSize));
      const finalSize = Math.min(sourceSize, imgNaturalWidth - sourceX, imgNaturalHeight - sourceY);
      
      // Draw the image portion to fill the circle
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        finalSize,
        finalSize,
        0,
        0,
        cropCircleSize,
        cropCircleSize
      );
      
      // Convert to blob
      canvas.toBlob(
        async (blob) => {
          if (blob) {
            const croppedFile = blobToFile(blob, avatarFile.name);
            
            // Update preview
            if (cropPreviewUrl) {
              revokePreviewUrl(cropPreviewUrl);
            }
            if (avatarPreview) {
              revokePreviewUrl(avatarPreview);
            }
            const newPreviewUrl = await createPreviewUrl(croppedFile);
            setCropPreviewUrl(null);
            setAvatarPreview(newPreviewUrl);
            setAvatarFile(croppedFile);
            setShowCropModal(false);
            setImagePosition({ x: 0, y: 0 });
            setImageScale(1);
            setCropRotation(0);
          } else {
            throw new Error('Failed to create cropped image');
          }
        },
        avatarFile.type || 'image/png',
        0.95
      );
    } catch (error) {
      console.error('Crop error:', error);
      setError(t('createGroupModal.cropErrorFallback'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Close crop modal
  const handleCloseCropModal = () => {
    if (!isProcessing) {
      if (cropPreviewUrl) {
        revokePreviewUrl(cropPreviewUrl);
        setCropPreviewUrl(null);
      }
      setShowCropModal(false);
      setImagePosition({ x: 0, y: 0 });
      setImageScale(1);
      setCropRotation(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setAvatarFile(null);
    }
  };

  // Upload avatar
  const uploadAvatar = async () => {
    if (!avatarFile || !authToken) return null;
    if (avatarFile.size > maxUploadBytes) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('createGroupModal.fileSizeLimitTitle'), message: UPGRADE_MESSAGES.uploadLimit },
      }));
      return null;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', avatarFile);

      const apiBaseUrl = await getApiBaseUrl();
      const uploadResponse = await fetch(
        `${apiBaseUrl}/chat/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        const errData = await uploadResponse.json().catch(() => ({}));
        const msg = uploadResponse.status === 413
          ? (errData?.message || UPGRADE_MESSAGES.uploadLimit)
          : (errData?.message || t('createGroupModal.avatarUploadFailedFallback'));
        throw new Error(msg);
      }

      const uploadResult = await uploadResponse.json();
      if (uploadResult?.ok && uploadResult?.fileUrl) {
        return uploadResult.fileUrl;
      }
      return null;
    } catch (error) {
      console.error('Avatar upload error:', error);
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Toggle member selection
  const toggleMember = (friendEmail) => {
    setSelectedMembers((prev) => {
      if (prev.includes(friendEmail)) {
        return prev.filter(email => email !== friendEmail);
      } else {
        return [...prev, friendEmail];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!groupName.trim()) {
      setError(t('createGroupModal.groupNameRequired'));
      return;
    }

    // Check group creation limit for Free users (backend will enforce, but show warning)
    if (!isPlusUser) {
      // Backend will check and return error if limit reached
      // We'll show Plus notification if backend returns limit error
    }

    setIsLoading(true);

    try {
      // Upload avatar if selected
      let avatarUrl = null;
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
        if (!avatarUrl) {
          // Avatar yükleme başarısız oldu, kullanıcıya uyarı göster ama devam et
          setError(t('createGroupModal.avatarUploadErrorContinue'));
          // Devam et, avatar olmadan grup oluştur
        }
      }

      // Create group
      const group = await createGroup(
        groupName.trim(),
        description.trim() || null,
        avatarUrl,
        selectedMembers
      );

      if (group) {
        setSuccess(true);
        setGroupName('');
        setDescription('');
        setSelectedMembers([]);
        setAvatarFile(null);
        if (avatarPreview) {
          revokePreviewUrl(avatarPreview);
        }
        setAvatarPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setError(null);
        }, 1500);
      } else {
        setError(t('createGroupModal.createFailedFallback'));
      }
    } catch (err) {
      console.error('Create group error:', err);
      
      // Error toast is already shown in ChatContext.js
      // Just set the error message for modal display
      const errorMessage = getApiErrorMessage(err, { fallback: t('createGroupModal.createFailedFallback') });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading && !uploadingAvatar && !isProcessing) {
      setGroupName('');
      setDescription('');
      setSelectedMembers([]);
      setAvatarFile(null);
      if (avatarPreview) {
        revokePreviewUrl(avatarPreview);
      }
      if (cropPreviewUrl) {
        revokePreviewUrl(cropPreviewUrl);
      }
      setAvatarPreview(null);
      setCropPreviewUrl(null);
      setShowCropModal(false);
      setImagePosition({ x: 0, y: 0 });
      setImageScale(1);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };


  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Crop Modal */}
          {showCropModal && (
            <div className="fixed inset-0 z-[100002] flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseCropModal}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              />

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-2xl bg-[var(--color-bg-elevated)] rounded-lg shadow-elevated overflow-hidden flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
                  <h2 className="text-xl font-semibold text-[var(--color-text-primary)] dark:text-white">
                    {t('createGroupModal.editPhotoTitle')}
                  </h2>
                  <button
                    onClick={handleCloseCropModal}
                    disabled={isProcessing}
                    className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors disabled:opacity-50"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Image Preview Area with Circular Crop */}
                <div
                  ref={cropContainerRef}
                  className="flex-1 flex items-center justify-center p-8 bg-[var(--color-bg-muted)] dark:bg-[var(--color-bg-base)]/50 overflow-hidden relative min-h-[500px]"
                >
                  {/* Circular crop container */}
                  <div
                    className="relative rounded-full overflow-hidden"
                    style={{
                      width: `${cropCircleSize}px`,
                      height: `${cropCircleSize}px`,
                      border: '4px solid white',
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.6), 0 0 20px rgba(0,0,0,0.3)',
                    }}
                  >
                    {cropPreviewUrl && (
                      <div
                        className="w-full h-full relative"
                        onMouseDown={handleCropMouseDown}
                        onMouseMove={handleCropMouseMove}
                        onMouseUp={handleCropMouseUp}
                        onMouseLeave={handleCropMouseUp}
                        style={{ 
                          cursor: isDragging ? 'grabbing' : 'grab',
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          ref={cropImageRef}
                          src={cropPreviewUrl}
                          alt={t('imagePreviewModal.previewAlt')}
                          className="absolute"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imageScale}) rotate(${cropRotation}deg)`,
                            transformOrigin: 'center center',
                            transition: isProcessing ? 'none' : 'transform 0.1s ease-out',
                            userSelect: 'none',
                          }}
                          draggable={false}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div className="p-4 border-t border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)]">
                  <div className="space-y-4">
                    {/* Zoom Control */}
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 min-w-[60px]">
                        {t('createGroupModal.zoomLabel')}
                      </span>
                      <div className="flex-1 flex items-center gap-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleZoom(-0.1)}
                          disabled={isProcessing || imageScale <= 0.5}
                          className="p-2 rounded-lg bg-[var(--color-bg-elevated)] dark:bg-surface hover:bg-[#24354f] dark:hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={t('createGroupModal.zoomOutTitle')}
                        >
                          <span className="text-lg font-bold">−</span>
                        </motion.button>
                        <div className="flex-1 bg-[#24354f] dark:bg-surface rounded-full h-2 relative">
                          <div
                            className="bg-[#2563eb] h-full rounded-full transition-all"
                            style={{ width: `${((imageScale - 0.5) / 2.5) * 100}%` }}
                          />
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleZoom(0.1)}
                          disabled={isProcessing || imageScale >= 3}
                          className="p-2 rounded-lg bg-[var(--color-bg-elevated)] dark:bg-surface hover:bg-[#24354f] dark:hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={t('createGroupModal.zoomInTitle')}
                        >
                          <span className="text-lg font-bold">+</span>
                        </motion.button>
                        <span className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] min-w-[50px] text-right">
                          {Math.round(imageScale * 100)}%
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between gap-4">
                      {/* Left controls */}
                      <div className="flex items-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleRotate(-1)}
                          disabled={isProcessing}
                          className="p-2 rounded-lg bg-[var(--color-bg-elevated)] dark:bg-surface hover:bg-[#24354f] dark:hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={t('imagePreviewModal.rotateLeft')}
                        >
                          <RotateCcw className="w-5 h-5" />
                        </motion.button>
                        
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleRotate(1)}
                          disabled={isProcessing}
                          className="p-2 rounded-lg bg-[var(--color-bg-elevated)] dark:bg-surface hover:bg-[#24354f] dark:hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={t('imagePreviewModal.rotateRight')}
                        >
                          <RotateCw className="w-5 h-5" />
                        </motion.button>
                      </div>

                      {/* Right controls */}
                      <div className="flex items-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleCloseCropModal}
                          disabled={isProcessing}
                          className="px-4 py-2 rounded-lg bg-[var(--color-bg-elevated)] dark:bg-surface hover:bg-[#24354f] dark:hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] dark:text-gray-300 transition-colors disabled:opacity-50"
                        >
                          {t('common:cancel')}
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleApplyCrop}
                          disabled={isProcessing}
                          className="px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-[#3d8ef0] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>{t('createGroupModal.applying')}</span>
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              <span>{t('createGroupModal.apply')}</span>
                            </>
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-elevated)] rounded-lg shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-[var(--color-text-primary)]">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">
                    {t('createGroupModal.newGroupTitle')}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isLoading || uploadingAvatar}
                  className="p-2 rounded-xl hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Group Avatar */}
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt={t('createGroupModal.avatarPreviewAlt')}
                        className="w-24 h-24 rounded-full object-cover border-4 border-[#3d8ef0]/50"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-[var(--color-text-primary)] border-4 border-[#3d8ef0]/50 text-white text-2xl font-bold">
                        {groupName.trim() ? getInitials(groupName) : <Users className="w-12 h-12" />}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading || uploadingAvatar}
                      className="absolute bottom-0 right-0 p-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-full shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarSelect}
                      className="hidden"
                    />
                  </div>
                  {uploadingAvatar && (
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t('createGroupModal.uploadingAvatar')}</span>
                    </div>
                  )}
                </div>

                {/* Group Name */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('createGroupModal.groupNameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder={t('createGroupModal.groupNamePlaceholder')}
                    disabled={isLoading || uploadingAvatar}
                    className="w-full px-4 py-3 bg-[var(--color-bg-base)] dark:bg-surface border border-white/10 dark:border-white/10 rounded-xl text-[var(--color-text-primary)] dark:text-white placeholder-[var(--color-text-muted)] dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    maxLength={100}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('createGroupModal.groupDescriptionLabel')} <span className="text-[var(--color-text-muted)] text-xs">{t('createGroupModal.optionalLabel')}</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('createGroupModal.descriptionPlaceholder')}
                    disabled={isLoading || uploadingAvatar}
                    rows={3}
                    className="w-full px-4 py-3 bg-[var(--color-bg-base)] dark:bg-surface border border-white/10 dark:border-white/10 rounded-xl text-[var(--color-text-primary)] dark:text-white placeholder-[var(--color-text-muted)] dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    maxLength={500}
                  />
                </div>

                {/* Member Selection */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('groupInfoSidebar.membersLabel')} <span className="text-[var(--color-text-muted)] text-xs">{t('createGroupModal.membersOptionalHint')}</span>
                  </label>

                  {availableFriends.length === 0 ? (
                    <div className="p-4 bg-[var(--color-bg-muted)] dark:bg-surface/50 rounded-xl text-center text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                      {t('createGroupModal.noFriendsHint')}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {availableFriends.map((friend) => {
                        const friendId = friend.email || friend.id;
                        const isSelected = selectedMembers.includes(friendId);
                        
                        return (
                          <button
                            key={friendId}
                            type="button"
                            onClick={() => toggleMember(friendId)}
                            disabled={isLoading || uploadingAvatar}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                              isSelected
                                ? 'bg-primary-50 dark:bg-[#2563eb]/10/20 border-2 border-[#3d8ef0]/50'
                                : 'bg-[var(--color-bg-muted)] dark:bg-surface/50 border-2 border-transparent hover:border-white/10 dark:hover:border-dark-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {/* Avatar */}
                            <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-sm font-semibold text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                              {friend.avatarUrl ? (
                                <img
                                  src={friend.avatarUrl}
                                  alt={getDisplayName(friend)}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                getInitials(getDisplayName(friend))
                              )}
                            </div>
                            
                            {/* Name */}
                            <div className="flex-1 text-left">
                              <p>
                                <Username user={friend} variant="default" />
                              </p>
                              <p className={`text-xs ${getPlusEmailColor(friend)}`}>
                                {friend.email}
                              </p>
                            </div>
                            
                            {/* Selection Indicator */}
                            {isSelected ? (
                              <UserMinus className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                            ) : (
                              <UserPlus className="w-5 h-5 text-[var(--color-text-muted)]" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {selectedMembers.length > 0 && (
                    <div className="mt-3 text-sm text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)]">
                      {t('createGroupModal.selectedCount', { count: selectedMembers.length })}
                    </div>
                  )}
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </motion.div>
                )}

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {t('createGroupModal.createdSuccessMessage')}
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 dark:border-white/10">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading || uploadingAvatar}
                  className="px-6 py-2.5 bg-[var(--color-bg-elevated)] dark:bg-surface hover:bg-[#24354f] dark:hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] dark:text-gray-300 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isLoading || uploadingAvatar || !groupName.trim()}
                  className="px-6 py-2.5 bg-primary text-[var(--color-text-primary)] font-semibold rounded-md transition-all shadow-elevated hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t('createGroupModal.creating')}</span>
                    </>
                  ) : (
                    t('createGroupModal.createButton')
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateGroupModal;
